import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'dense' | 'md' | 'hero' | 'touch' | 'icon' | 'icon-dense';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** In-flight text REPLACES the label (§11 copy voice): `Saving…`, never an appended spinner. */
  busy?: boolean;
  busyLabel?: string;
  /** Required for icon-only buttons: becomes the accessible name AND the tooltip (named by construction). */
  label?: string;
  icon?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-brand-fg hover:bg-brand-hover font-semibold',
  secondary: 'bg-surface text-ink ring-1 ring-line-strong hover:ring-brand hover:text-ink',
  ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
  danger: 'bg-st-danger text-on-danger hover:opacity-90 font-semibold',
  link: 'text-ink underline decoration-brand decoration-2 underline-offset-4 hover:decoration-brand-hover px-0',
};
const sizes: Record<Size, string> = {
  dense: 'h-control-dense px-2.5 text-[13px] gap-1.5',
  md: 'h-control px-3.5 text-sm gap-2',
  hero: 'h-control-hero px-5 text-[15px] gap-2',
  touch: 'h-control-touch px-5 text-base gap-2',
  icon: 'size-9 justify-center',
  'icon-dense': 'size-7 justify-center',
};

/** The same look for a link that navigates (a `<Link>` styled as a button — still a link to assistive tech). */
export function buttonClass(variant: Variant = 'secondary', size: Size = 'md', className?: string): string {
  return cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-control transition-colors dur-fast ease-out', variants[variant], sizes[size], className);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', busy, busyLabel, label, icon, className, children, disabled, title, ...rest },
  ref,
) {
  const iconOnly = !children && Boolean(icon);
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-label={iconOnly ? label : rest['aria-label']}
      title={title ?? (iconOnly ? label : undefined)}
      className={buttonClass(variant, size, cn('disabled:cursor-not-allowed disabled:opacity-55', className))}
      {...rest}
    >
      {icon}
      {busy ? (busyLabel ?? 'Working…') : children}
    </button>
  );
});
